import React, { useEffect, useRef, useState } from "react";
import { X, Save } from "lucide-react";

/**
 * EditCustomerDetailsModal — edit customer contact info + tradie-internal
 * notes for a single lead. Updates flow through useLeads.updateLeadContactDetails
 * which writes to the leads table — the canonical source of truth that
 * quote PDFs, invoices, and customer emails all read from. So one save here
 * propagates everywhere without further sync.
 */
export default function EditCustomerDetailsModal({
  lead,
  onSave,
  onClose,
}) {
  const initial = {
    customer_name: lead?.customer || lead?.form?.fullName || "",
    customer_phone: lead?.form?.phone || "",
    customer_email: lead?.form?.email || "",
    address: lead?.form?.address || "",
    suburb: lead?.suburb || lead?.form?.suburb || "",
    postcode: lead?.postcode || lead?.form?.postcode || "",
  };

  const [fields, setFields] = useState(initial);
  const [saving, setSaving] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Esc to close
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function update(key, value) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  // Compute only-changed-fields payload — avoids overwriting columns we
  // didn't intentionally touch.
  function diff() {
    const out = {};
    for (const k of Object.keys(initial)) {
      if (fields[k] !== initial[k]) out[k] = fields[k];
    }
    return out;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    const payload = diff();
    if (Object.keys(payload).length === 0) {
      onClose?.();
      return;
    }
    setSaving(true);
    const result = await onSave?.(lead.id, payload);
    setSaving(false);
    if (result?.ok !== false) onClose?.();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit customer contact"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "440px",
          maxHeight: "92vh",
          overflowY: "auto",
          background: "var(--surface, #fff)",
          color: "var(--text)",
          borderRadius: "16px",
          boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
          padding: "18px 20px 20px",
          border: "1px solid var(--border, rgba(140,100,80,0.18))",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "14px",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 700 }}>
            Edit customer contact
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-soft, #6b5448)",
              cursor: "pointer",
              padding: "4px",
              borderRadius: "6px",
            }}
          >
            <X size={18} />
          </button>
        </div>

        <Field
          label="Name"
          value={fields.customer_name}
          onChange={(v) => update("customer_name", v)}
          inputRef={firstInputRef}
        />
        <Field
          label="Phone"
          value={fields.customer_phone}
          onChange={(v) => update("customer_phone", v)}
          type="tel"
        />
        <Field
          label="Email"
          value={fields.customer_email}
          onChange={(v) => update("customer_email", v)}
          type="email"
        />
        <Field
          label="Address"
          value={fields.address}
          onChange={(v) => update("address", v)}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 100px",
            gap: "10px",
          }}
        >
          <Field
            label="Suburb"
            value={fields.suburb}
            onChange={(v) => update("suburb", v)}
          />
          <Field
            label="Postcode"
            value={fields.postcode}
            onChange={(v) => update("postcode", v)}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "flex-end",
            marginTop: "16px",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            style={{ padding: "7px 14px", fontSize: "0.85rem" }}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            style={{
              padding: "7px 14px",
              fontSize: "0.85rem",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            disabled={saving}
          >
            <Save size={14} />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", inputRef }) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: "10px",
        fontSize: "0.78rem",
        fontWeight: 600,
        color: "var(--text-soft, #6b5448)",
      }}
    >
      {label}
      <input
        ref={inputRef}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          display: "block",
          width: "100%",
          padding: "8px 10px",
          marginTop: "4px",
          fontSize: "0.9rem",
          borderRadius: "8px",
          border: "1px solid var(--border, rgba(140,100,80,0.18))",
          background: "var(--bg, #fafafa)",
          color: "var(--text)",
          fontFamily: "inherit",
        }}
      />
    </label>
  );
}
